import { dispositionBucket, isQueueCall, recordMatchesExtension } from '@/utils/callRecords';
import type { CallRecord } from '@/types';
import { missedCalls, type ClassifiedCall } from '@/lib/reports/callClassification';

export type StatCallKind = 'received' | 'made' | 'total' | 'missed';

export const STAT_CALL_TITLES: Record<StatCallKind, string> = {
  received: 'Received calls',
  made: 'Calls made',
  total: 'All calls',
  missed: 'Missed calls and voicemail',
};

/**
 * The CDR rows behind a Today's Stats card, using the same rules as the per-extension
 * counts: a row counts when it involves one of the extensions (so "Play Prompt" legs
 * and calls that never reached an extension are left out), and missed means an inbound
 * row that was not answered. Missed also takes unanswered call queue rows: Yeastar logs
 * no per-agent row for a queue call nobody picked up, so these are not double counted. A queue
 * row is left out when the same call (uid) was answered later, e.g. after overflowing to
 * another queue.
 */
export function filterStatCalls(
  records: CallRecord[],
  kind: StatCallKind,
  extensionNumbers: string[]
): CallRecord[] {
  const answered = kind === 'missed' ? answeredCallUids(records) : new Set<string>();
  return records.filter((record) => {
    const inbound = record.call_type === 'Inbound';
    const outbound = record.call_type === 'Outbound';
    if (kind === 'made' ? !outbound : kind === 'total' ? !inbound && !outbound : !inbound) {
      return false;
    }
    if (kind === 'missed' && dispositionBucket(record) === 'answered') return false;
    if (isQueueCall(record)) return kind === 'missed' && !answered.has(record.uid || '');
    return extensionNumbers.some((number) => recordMatchesExtension(record, number));
  });
}

/**
 * Calls someone picked up in the end (an extension, or a queue agent; an IVR answering does not
 * count). A queue that rings out and overflows to another queue, where an agent answers, is not
 * a missed call.
 */
function answeredCallUids(records: CallRecord[]): Set<string> {
  const uids = new Set<string>();
  for (const record of records) {
    if (record.uid && dispositionBucket(record) === 'answered' && !/^IVR\s/i.test(String(record.call_to || ''))) {
      uids.add(record.uid);
    }
  }
  return uids;
}

/** Inbound queue calls nobody answered: the queue part of the Missed card. */
export function countQueueMissed(records: CallRecord[]): number {
  const answered = answeredCallUids(records);
  return records.filter(
    (record) =>
      record.call_type === 'Inbound' &&
      isQueueCall(record) &&
      dispositionBucket(record) !== 'answered' &&
      !answered.has(record.uid || '')
  ).length;
}

/**
 * The calls behind a Today's Stats card, one per call (uid), from the same classification as the
 * card itself. Short IVR hang-ups are left out, as they are from the cards.
 */
export function filterClassifiedCalls(calls: ClassifiedCall[], kind: StatCallKind): ClassifiedCall[] {
  switch (kind) {
    case 'received':
      return calls.filter((call) => call.direction === 'inbound' && !call.shortSpam);
    case 'made':
      return calls.filter((call) => call.direction === 'outbound');
    case 'total':
      return calls.filter((call) => call.direction === 'outbound' || (call.direction === 'inbound' && !call.shortSpam));
    case 'missed':
      return missedCalls(calls);
  }
}
